import {type Component} from 'vue';
import {type RouteLocationStringParam, type RouteRecordRaw} from 'vue-router';
import {toInt} from '../library/math';
import {calendarResourceOptions} from '../library/modules/calendar/options';

const calendarEdit                     = () => import('../views/calendar/CalendarEdit.vue');
const CalendarList                     = () => import('../views/calendar/list/CalendarList.vue');
const CalendarEntryEdit                = () => import('../views/calendar/entry/CalendarEntryEdit.vue');
const CalendarEntryRequest             = () => import('../views/calendar/entry/CalendarEntryRequest.vue');
const CalendarEntryView                = () => import('../views/calendar/entry/CalendarEntryView.vue');
const CalendarSettings                 = () => import('../views/calendar/CalendarSettings.vue');
const CalendarSettingsExport           = () => import('../views/calendar/settings/CalendarSettingsExport.vue');
const CalendarSettingsReservationMail  = () => import('../views/calendar/settings/CalendarSettingsReservationMail.vue');
const CalendarSettingsAttachCancelLink = () => import('../views/calendar/settings/CalendarSettingsAttachCancelLink.vue');
const CalendarEntryRequestCancel       = () => import('../views/calendar/CalendarEntryRequestCancel.vue');
const CategoryManagement               = () => import('../components/CategoryManagement.vue');

interface CalendarComponents
{
    calendarEdit: Component;
    calendarList: Component;
    entryEdit: Component;
    entryRequest: Component;
    entryView: Component;
}

let defaultComponents: CalendarComponents = {
    calendarEdit: calendarEdit,
    calendarList: CalendarList,
    entryEdit: CalendarEntryEdit,
    entryRequest: CalendarEntryRequest,
    entryView: CalendarEntryView,
};

export function addCalendarRoutes(resource: CalendarResources, routes: RouteRecordRaw[], overwriteComponents: Partial<CalendarComponents> = {}, metaData: MetaData = {} as MetaData)
{
    const meta: MetaData = {
        ...metaData,
        module: resource,
    };

    const components: CalendarComponents = Object.assign({}, defaultComponents, overwriteComponents);

    //<editor-fold desc="calendar">
    // calendar list
    routes.push(
        // old compatibility route
        {
            path: '/' + resource + '/calendar/list/id/:id',
            redirect: {name: resource + '-list'},
            meta,
        },
        // old compatibility route
        {
            path: '/' + resource + '/calendar/list/id/:id/:wildcard*',
            redirect: {name: resource + '-list'},
            meta,
        },
        // old compatibility route
        {
            path: '/' + resource + '/calendar/overview-list/id/:id',
            alias: '/' + resource + '/calendar/overview-list/id/:id/:wildcard*',
            redirect(to)
            {
                return {
                    name: resource + '-list',
                    params: {
                        type: 'table',
                        id: to.params.id,
                    },
                };
            },
            meta,
        },
        {
            path: '/' + resource,
            redirect: {name: resource + '-list'},
            meta,
        },
        {
            name: resource + '-request-list',
            component: components.calendarList,
            path: '/' + resource + '/calendar/list/:id(\\d+)?/:type(request)/:state?',
            props(route: RouteLocationStringParam)
            {
                return {
                    resource,
                    id: toInt(route.params.id),
                    type: route.params.type,
                    state: route.params.state || 'open',
                };
            },
            meta,
        },
        {
            path: '/' + resource + '/calendar/list/:date(today|\\d{4}-\\d{2}-\\d{2})/:type?',
            redirect(to)
            {
                return {
                    name: resource + '-list',
                    params: {
                        id: '0',
                        type: to.params.type,
                        date: to.params.date,
                    },
                };
            },
            meta,
        },
        {
            name: resource + '-list',
            component: components.calendarList,
            path: '/' + resource + '/calendar/list/:id(\\d+)?/:type?/:date?',
            alias: '/' + resource + '/calendar/export-list/id/:id(\\d+)?/view/:type?',
            props(route: RouteLocationStringParam)
            {
                return {
                    resource,
                    id: toInt(route.params.id),
                    type: route.params.type,
                    date: route.params.date,
                };
            },
            meta,
        },
    );

    routes.push(
        {
            name: resource + '-add',
            component: components.calendarEdit,
            path: '/' + resource + '/:type(group|calendar|link)/add/:parent?',
            props(route: RouteLocationStringParam)
            {
                return {
                    type: route.params.type,
                    resource,
                    parent: toInt(route.params.parent),
                };
            },
            meta,
        },
        {
            name: resource + '-edit',
            component: components.calendarEdit,
            path: '/' + resource + '/:type(group|calendar|link)/edit/:id(\\d+)?',
            props(route: RouteLocationStringParam)
            {
                return {
                    type: route.params.type,
                    resource,
                    id: toInt(route.params.id),
                };
            },
            meta,
        },
    );

    //</editor-fold>

    const dateRegex = '\\d{4}-\\d{2}-\\d{2} \\d{2}:\\d{2}|\\d{4}-\\d{2}-\\d{2}';

    //<editor-fold desc="entry">
    // entry add routes
    routes.push(
        // old compatibility route
        {
            path: '/' + resource + '/entry/edit/parent/:calendar/:wildcard*',
            redirect: {name: resource + '-entry-add'},
            meta,
        },
        // old compatibility route
        {
            path: '/' + resource + '/entry/edit/calendar/:calendar/:wildcard*',
            redirect: {name: resource + '-entry-add'},
            meta,
        },
        {
            name: resource + '-entry-request',
            component: components.entryRequest,
            path: '/' + resource + '/entry/request/:calendar',
            props(route: RouteLocationStringParam)
            {
                return {
                    resource,
                    calendar: toInt(route.params.calendar),
                };
            },
            meta,
        },
        {
            name: resource + '-entry-add',
            component: components.entryEdit,
            path: '/' + resource + '/entry/add/:calendar(\\d+)?/:start(' + dateRegex + ')?/:end(' + dateRegex + ')?/:allDay(0|1)?/:institution(\\d+)?',
            alias: ['/' + resource + '/entry/add/:calendar?/:wildcard*', '/' + resource + '/entry/edit'],
            props(route: RouteLocationStringParam)
            {
                return {
                    resource,
                    calendar: toInt(route.params.calendar),
                    institution: toInt(route.params.institution),
                };
            },
            meta,
        },
    );

    // entry edit routes
    routes.push(
        // old compatibility route
        {
            path: '/' + resource + '/entry/edit/id/:id/start/:start',
            redirect: {name: resource + '-entry-edit'},
            meta,
        },
        // old compatibility route
        {
            path: '/' + resource + '/entry/edit/id/:id/start/:start/:wildcard*',
            redirect: {name: resource + '-entry-edit'},
            meta,
        },
        // old compatibility route
        {
            path: '/' + resource + '/entry/edit/id/:id/:wildcard*',
            redirect: {name: resource + '-entry-edit'},
            meta,
        },
        {
            name: resource + '-entry-edit',
            component: components.entryEdit,
            path: '/' + resource + '/entry/edit/:id/:start?',
            alias: ['/' + resource + '/entry/edit/:id/:start?/:wildcard*'],
            props(route: RouteLocationStringParam)
            {
                return {
                    resource,
                    id: parseInt(route.params.id),
                    start: route.params.start || '',
                };
            },
            meta,
        },
        {
            name: resource + '-entry-copy',
            component: components.entryEdit,
            path: '/' + resource + '/entry/copy/:id/:start?',
            alias: ['/' + resource + '/entry/copy/:id/:start?/:wildcard*'],
            props(route: RouteLocationStringParam)
            {
                return {
                    resource,
                    id: parseInt(route.params.id),
                    start: route.params.start || '',
                };
            },
        },
    );

    // entry view routes
    routes.push(
        // old compatibility route
        {
            path: '/' + resource + '/entry/view/id/:id/start/:start',
            redirect: {name: resource + '-entry-view'},
            meta,
        },
        // old compatibility route
        {
            path: '/' + resource + '/entry/view/id/:id/start/:start/:wildcard*',
            redirect: {name: resource + '-entry-view'},
            meta,
        },
        // old compatibility route
        {
            path: '/' + resource + '/entry/view/id/:id/:wildcard*',
            redirect: {name: resource + '-entry-view'},
            meta,
        },
        {
            name: resource + '-entry-view',
            component: components.entryView,
            path: '/' + resource + '/entry/view/:id/:start?',
            alias: ['/' + resource + '/entry/view/:id/:start?/:wildcard*'],
            props(route: RouteLocationStringParam)
            {
                return {
                    resource,
                    id: parseInt(route.params.id),
                    start: route.params.start || '',
                };
            },
            meta,
        },
    );
    //</editor-fold>

    if (['sportsarea', 'equipment', 'roommanagement', 'vehicle'].includes(resource))
    {
        routes.push(
            {
                path: '/' + resource + '/settings',
                redirect: {name: resource + '-settings-export'},
                name: resource + '-settings',
                component: CalendarSettings,
                meta,
                alias: [
                    '/' + resource + '/settings/:wildcard*',
                ],
                children: [
                    {
                        path: 'export',
                        name: resource + '-settings-export',
                        component: CalendarSettingsExport,
                        meta,
                    },
                    {
                        path: 'reservation-mail',
                        name: resource + '-settings-reservation-mail',
                        component: CalendarSettingsReservationMail,
                        meta,
                    },
                    {
                        path: 'attach-cancel-link',
                        name: resource + '-settings-attach-cancel-link',
                        component: CalendarSettingsAttachCancelLink,
                        meta,
                    },
                    {
                        path: 'categories',
                        name: resource + '-settings-categories',
                        component: CategoryManagement,
                        props()
                        {
                            return {
                                tag: 'div',
                                breadcrumbs: [],
                                route: '/' + resource + '/categories',
                            };
                        },
                        meta,
                    },
                ],
            },
            {
                name: resource + '-entry-request-cancel',
                path: '/' + resource + '/entry/request-cancel/:id/:code',
                component: CalendarEntryRequestCancel,
                props(route: RouteLocationStringParam)
                {
                    return {
                        resource,
                        id: route.params.id,
                        code: route.params.code,
                    };
                },
                meta: Object.assign({}, meta, {
                    layout: 'minimal',
                    allowLoggedOut: true,
                }),
            },
        );
    }

    routes.push(
        {
            path: '/' + resource + '/:wildcard*',
            redirect: {name: resource + '-list'},
            meta,
        },
    );
}